import { useEffect, useRef } from "react";
import { diagnosticsToView } from "./diagnosticsToView";
import { viewOverrideId } from "./viewOverrideId";
import { useGeotabClient } from "@/components/GeotabClientContext";
import AddInIds from "@/constants/AddInIds";
import { presetUserId } from "@/hooks/addins/presetUserId";
import { useAddinData } from "@/hooks/addins/useAddinData";
import { useUpdateAddIn } from "@/hooks/addins/useUpdateAddIn";
import { DataGridSettingsAddInData } from "@/models/DataGridSettingsAddInData";
import { DiagnosticView, ViewOverride, ViewPageType } from "@/models/DiagnosticView";
import { Preset } from "@/models/Preset";

const MUTEX_KEY = "diagViewsMigrating";
const TEMPLATE_PREFIX = "template-";

interface SourceTemplate {
  template: DataGridSettingsAddInData;
  pageType: ViewPageType;
}

function isTemplate(d: DataGridSettingsAddInData): boolean {
  return d.id.startsWith(TEMPLATE_PREFIX);
}

function templateBelongsToPreset(templateId: string, presetDescription: string): boolean {
  return templateId.startsWith(`${TEMPLATE_PREFIX}${presetDescription}-`);
}

export function useViewMigration() {
  const { me } = useGeotabClient();
  const ran = useRef(false);

  const { data: views = [] } = useAddinData<DiagnosticView>(AddInIds.diagnosticViews);
  const { data: presets = [] } = useAddinData<Preset>(AddInIds.presets);
  const { data: smtsRows = [] } = useAddinData<DataGridSettingsAddInData>(
    AddInIds.statusMonitorTableSetting,
  );
  const { data: dsdtsRows = [] } = useAddinData<DataGridSettingsAddInData>(
    AddInIds.deviceStatusDataTableSettings,
  );

  const writeView = useUpdateAddIn<DiagnosticView>(AddInIds.diagnosticViews, "id");
  const writeSmtsOverride = useUpdateAddIn<ViewOverride>(AddInIds.statusMonitorTableSetting, "id");
  const writeDsdtsOverride = useUpdateAddIn<ViewOverride>(
    AddInIds.deviceStatusDataTableSettings,
    "id",
  );

  useEffect(() => {
    if (ran.current) return;
    if (sessionStorage.getItem(MUTEX_KEY)) return;
    if (views.length > 0) return;
    if (presets.length === 0) return;
    if (!me?.id) return;
    ran.current = true;
    sessionStorage.setItem(MUTEX_KEY, "1");

    const userId = me.id;

    async function migrateUserStateIfPresent(
      preset: Preset,
      view: DiagnosticView,
      pageType: ViewPageType,
    ) {
      const rows = pageType === "diagnosticMonitor" ? smtsRows : dsdtsRows;
      const writer =
        pageType === "diagnosticMonitor" ? writeSmtsOverride : writeDsdtsOverride;
      const userStateId = presetUserId(userId, preset.id);
      const existing = rows.find(r => r.id === userStateId);
      if (!existing) return;
      await writer.mutateAsync({
        id: viewOverrideId(userId, view.id),
        viewId: view.id,
        baseUpdatedAt: view.updatedAt,
        gridStateDelta: existing.dataGridState ?? "{}",
      });
    }

    (async () => {
      try {
        const migratedKeys = new Set(
          views
            .map(v =>
              v.migratedFrom
                ? `${v.migratedFrom.presetId}::${v.migratedFrom.templateId ?? ""}`
                : null,
            )
            .filter((k): k is string => k !== null),
        );

        const sources: SourceTemplate[] = [
          ...smtsRows
            .filter(isTemplate)
            .map(t => ({ template: t, pageType: "diagnosticMonitor" as ViewPageType })),
          ...dsdtsRows
            .filter(isTemplate)
            .map(t => ({ template: t, pageType: "statusMonitor" as ViewPageType })),
        ];

        for (const preset of presets) {
          const desc = preset.description ?? "";
          const matching = sources.filter(s => templateBelongsToPreset(s.template.id, desc));

          if (matching.length === 0) {
            const key = `${preset.id}::`;
            if (migratedKeys.has(key)) continue;
            const view = diagnosticsToView({
              preset,
              template: null,
              pageTypes: ["diagnosticMonitor", "statusMonitor"],
            });
            await writeView.mutateAsync(view);
            await migrateUserStateIfPresent(preset, view, "diagnosticMonitor");
            await migrateUserStateIfPresent(preset, view, "statusMonitor");
            continue;
          }

          for (const src of matching) {
            const key = `${preset.id}::${src.template.id}`;
            if (migratedKeys.has(key)) continue;
            const view = diagnosticsToView({
              preset,
              template: src.template,
              pageTypes: [src.pageType],
            });
            await writeView.mutateAsync(view);
            await migrateUserStateIfPresent(preset, view, src.pageType);
          }
        }
      } finally {
        sessionStorage.removeItem(MUTEX_KEY);
      }
    })();
  }, [
    views,
    presets,
    smtsRows,
    dsdtsRows,
    me?.id,
    writeView,
    writeSmtsOverride,
    writeDsdtsOverride,
  ]);
}
